import React, { useContext, useEffect, useState } from 'react';
import { Alert, AlertProps, Box, Button, ButtonDropdown, ExpandableSection, Header, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-react';
import { TPELoadingSpinner } from 'src/components/shared/TPELoadingSpinner';
import { CalculationBuilderContext } from '../CalculationBuilderView';
import PLITemplateSelector from './PLITemplateSelector';
import PLIConfigState, { initialState } from 'src/services/calculation-builder/PLIConfigState';
import ServiceCollection from 'src/services/ServiceCollection';
import TPEAction from 'src/models/common/TPEAction';
import { pliConfigReducer, ACTIONS } from 'src/services/calculation-builder/PLIConfigReducer';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import { PLIConfigTemplateSection, PLITemplateType } from 'src/models/calculation-builder/pli-config/PLIConfigTemplate';
import PLITemplateSection from './PLITemplateSection';
import StringUtils from 'src/utils/stringUtils';
import TPEErrorWatcher from 'src/components/shared/TPEErrorWatcher';
import { ACTION_TYPE, TPEBasicModal } from 'src/components/shared/TPEBasicModal';
import ArrayUtils from 'src/utils/arrayUtils';
import { ExpressionPartTypes } from 'src/models/calculations/CalculationDetailsResult';
import EntityStatusUtil from 'src/services/common/EntityStatusUtil';
import CONSTANTS from 'src/utils/constants';
import CloneToTherCLIsModal from './CloneToOtherCLIsModal';
import CommentsModal from '../CommentsModal';
import TemporaryMessage from 'src/models/common/TemporaryMessage';
import { CalcBuilderComment } from 'src/models/calculation-builder/CalcBuilderComment';
import { GlobalAppContext } from 'src/components/App';
import {CUSTOM_PLI} from "src/components/calculation-builder/pli-config/Constants";

export type ContextType = {
    state: PLIConfigState,
    dispatch: React.Dispatch<TPEAction>,
    services: ServiceCollection,
}

const PLIConfigProvider = (props: any) => {
    const { state, dispatch, services, children } = props;
    const providerValue = React.useMemo(() => ({
        state, dispatch, services
    }), [state, dispatch]);
    return (
        <PLIConfigContext.Provider value={providerValue}>
            {children}
        </PLIConfigContext.Provider>
    );
}

export const PLIConfigContext = React.createContext(null as unknown as ContextType);

const enum ConfigurePLITypes {
    SingleCLI = "SingleCLI",
    CloneToMultipleCLIs = "CloneToMultipleCLIs"
}

const defaultTopAlertMessage = {message:"Default Apttus suggested PLI is selected based on the calculation template you are using.", header: "", type:"info" as AlertProps.Type};
const successfulSingleCLITopAlertMessage = {message:"It will not impact on other CLIs linked to the same calculation template.", header: "PLI configuration saved to this CLI only", type:"success" as AlertProps.Type};
const successfulCloneAllPLITopAlertMessage = {message:"All the CLIs linked to the template now have the same PLI configuration.", header: "PLI configuration cloned to the all CLIs", type:"success" as AlertProps.Type};
const successfulCloneSelectedPLITopAlertMessage = {message:"All the selected CLIs now have the same PLI configuration.", header: "PLI configuration cloned to the selected CLIs", type:"success" as AlertProps.Type};

const SUCCESSFUL_COMMENT_ALERT = {
    message: "The comment was added successfully",
    type: "success",
    visible: true,
};

export default function PLIConfigContainer(props: { expanded?: boolean, onCancel: () => void, onSave?: (e: any) => void }) {
    const { expanded = false, onCancel, onSave } = props;
    const { userProfile } = useContext(GlobalAppContext);
    const { services, calcBuilderState, calcBuilderDispatch } = useContext(CalculationBuilderContext);
    const { calculation, selectedCalculationVersion, calculationTemplateMetaData, dataSourceRecords, steps: calculationSteps } = calcBuilderState;
    const [ state, dispatch ] = useReducerWithLogger(pliConfigReducer, initialState);
    const { pliTemplates, pliConfigMetadata, templateSections, updateStepPayload, allSectionsAreSaved, stepsThatNeedSaving, removeStepModalPayload, 
        deleteStepPayload, commentsHistory, commentsNeedRefresh, commentsModalPayload, addCommentPayload, selectedPLITemplate, allSectionsAreValid } = state;
    const [showActions, setShowActions] = useState<boolean>(expanded);
    const [createPLIConfigPayload, setCreatePLIConfigPayload] = useState<any>(null);
    const [validatePLIConfigPayload, setValidatePLIConfigPayload] = useState<{calculationNumber: string, calculationVersion: number} | null>(null);
    const [runPLIConfigPayload, setRunPLIConfigPayload] = useState<{calculationNumber: string, pliVersion: number} | null>(null);
    const [configurePLIForSingleCLIPayload, setConfigurePLIForSingleCLIPayload] = useState<{calculationNumber: string} | null>(null);
    const [topAlertMessage, setTopAlertMessage] = useState<{message:string, header: string, type:AlertProps.Type, action?:any} | null>(defaultTopAlertMessage)
    const [showCloneModal, setShowCloneModal] = useState(false);
    const [clonePLIPayload, setClonePLIPayload] = useState<{calculationNumber:string, cloneToAllCLIsInSameTemplate:boolean, targetCalculationNumbers:string[]}|null>(null);
    const [switchPLITemplatePayload, setSwitchPLITemplatePayload] = useState<{calculationNumber:string, newPLI:string}|null>(null);

    const [loadTime, setLoadTime] = useState<string|undefined>(undefined);
    const [generalPliConfigError, setGeneralPliConfigError] = useState<string|undefined>(undefined);
    const [invalidRunModalPayload, setInvalidRunModalPayload] = useState<{dataSourcesInProgress: string[], dataSourcesWithNoBalance: string[]} | null>(null);
    const [overviewSection, setOverviewSection] = useState<PLIConfigTemplateSection|undefined>(undefined);
    const [otherSections, setOtherSections] = useState<PLIConfigTemplateSection[]>([]);
    const [commentsModalMessage, setCommentsModalMessage] = React.useState(undefined as TemporaryMessage | undefined);
    const [currentUnsavedCommentIndex, setCurrentUnsavedCommentIndex] = React.useState(-1);
    const [unsavedCommentRequest, setUnsavedCommentRequest] = React.useState(undefined as CalcBuilderComment | undefined);
    
    const [pliTemplatesResult, pliTemplatesLoading, pliTemplatesError] = services.pliConfigService.getPLITemplates(calculation?.calculationNumber, loadTime);
    const [createPLIConfigResult, isCreatingPLIConfig, createPLIConfigError] = services.pliConfigService.createPLIConfig(createPLIConfigPayload);
    const [pliMetadataResult, pliMetadataLoading, pliMetadataError] = services.pliConfigService.getPLIConfigMetadata(loadTime, calculation?.calculationNumber);
    const [pliConfigResult, pliConfigLoading, pliConfigError] = services.pliConfigService.getPLIConfig(loadTime, calculation?.calculationNumber, pliConfigMetadata?.pliVersion);
    const [saveStepResult, stepIsSaving, saveStepError] = services.pliConfigService.savePLIConfigStep(calculation?.calculationNumber, updateStepPayload);
    const [validatePLIResult, isValidating, validatePLIError] = services.pliConfigService.validatePLIConfig(validatePLIConfigPayload);
    const [runPLIResult, isRunning, runPLIError] = services.pliConfigService.runPLIConfig(runPLIConfigPayload);
    const [deleteStepResult, isDeletingStep, deleteStepError] = services.pliConfigService.deletePLIConfigStep(deleteStepPayload);
    const [configureSingleCLIResult, isConfiguringSingleCLI, configureSingleCLIError] = services.pliConfigService.configurePLIForSingleCLI(configurePLIForSingleCLIPayload);
    const [clonePLIConfigResult, isCloningPLI, clonePLIError] = services.pliConfigService.clonePLIConfiguration(clonePLIPayload, CONSTANTS.CALCULATION_STATUS.DRAFT_IN_PROGRESS === pliConfigMetadata?.pliStatus);
    const [switchPLITemplateResult, isSwitchingPLI, switchPLITemplateError] = services.pliConfigService.switchPLITemplate(switchPLITemplatePayload);
    const [saveStepCommentsResult, isSavingStepComments, saveStepCommentsError] = services.pliConfigService.saveStepComment(addCommentPayload);
    const [getStepCommentsResult, isLoadingStepComments, getStepCommentsError] = services.pliConfigService.getStepComments(calculation?.calculationNumber, selectedCalculationVersion, commentsModalPayload?.step.stepId, commentsNeedRefresh);
    const [unsavedCommentResult, unsavedCommentLoading, unsavedCommentError] = services.pliConfigService.saveStepComment(unsavedCommentRequest);

    const reloadPLIConfig = React.useCallback(() => setLoadTime(new Date().toISOString()), []);

    useEffect(() => {
        setLoadTime(new Date().toISOString())
    },[])

    useEffect(() => {
        if (pliTemplatesResult != null) {
            dispatch(ACTIONS.SET_PLI_TEMPLATES.withPayload(pliTemplatesResult))
        }
    },[pliTemplatesResult]);

    useEffect(() => {
        if (!StringUtils.isNullOrEmpty(pliTemplatesError))
        console.error(pliTemplatesError);
    },[pliTemplatesError])

    useEffect(() => {
        if (pliMetadataResult != null) {
            dispatch(ACTIONS.SET_PLI_CONFIG_METADATA.withPayload(pliMetadataResult));
            if (pliMetadataResult.pli == null){
                setCreatePLIConfigPayload({calculationNumber: pliMetadataResult.calculationNumber})
            }
        }
    },[pliMetadataResult])

    useEffect(() => {
        if (createPLIConfigResult != null) {
            dispatch(ACTIONS.SET_PLI_CONFIG_METADATA.withPayload(createPLIConfigResult));
        }
    },[createPLIConfigResult])

    useEffect(() => {
        if (pliConfigResult != null) {
            dispatch(ACTIONS.SET_PLI_CONFIG.withPayload(pliConfigResult))
        }
    },[pliConfigResult])
    
    useEffect(() => {
        if (validatePLIResult != null) {
            dispatch(ACTIONS.SET_PLI_CONFIG.withPayload(validatePLIResult))
        }
    },[validatePLIResult])
    
    useEffect(() => {
        if (runPLIResult.length > 0) {
            dispatch(ACTIONS.UPDATE_SETPS_WITH_RUN_RESULTS.withPayload(runPLIResult))
        }
    },[runPLIResult])

    useEffect(() => {
        if (saveStepCommentsResult == null){
            return;
        }

        setCommentsModalMessage(SUCCESSFUL_COMMENT_ALERT);
        dispatch(ACTIONS.ADD_STEP_COMMENT.withPayload(saveStepCommentsResult));
    }, [saveStepCommentsResult]);
    
    useEffect(() => {
        if (saveStepResult != null) {
            dispatch(ACTIONS.REFRESH_STEP.withPayload(saveStepResult))
            if (stepsThatNeedSaving.length > 0){
                dispatch(ACTIONS.SAVE_ALL_SECTIONS);
            }
            else {
                setValidatePLIConfigPayload({
                    calculationNumber: calculation?.calculationNumber || '', 
                    calculationVersion: CONSTANTS.CALCULATION_STATUS.ACTIVE == calculationTemplateMetaData?.calculationStatus? 
                        CONSTANTS.CALCULATION_VERSIONS.CURRENT_VERSION : 
                        CONSTANTS.CALCULATION_VERSIONS.DRAFT_VERSION
                    })
            }
            //If saved a step on an active PLI then most likely the pliStatus and pliVersion has changed
            // So triggeting this update to refresh the PLI metadata
            if (CONSTANTS.ACTIVE_STATUS == pliConfigMetadata?.pliStatus) {
                setLoadTime(new Date().toISOString());
            }
        }
    },[saveStepResult, stepsThatNeedSaving])
    
    useEffect(() => {        
        setOverviewSection(templateSections.find(s => s.isOverview));
        setOtherSections(templateSections.filter(s => !s.isOverview));
    },[templateSections, loadTime])

    useEffect(() => {
        if (deleteStepResult == null){
            return;
        }              
        setLoadTime(new Date().toISOString())
        dispatch(ACTIONS.SET_REMOVE_STEP_PAYLOAD.withPayload(null));
    }, [deleteStepResult])
    
    useEffect(() => {
        if (configureSingleCLIResult == null){
            return;
        }              
        setTopAlertMessage(successfulSingleCLITopAlertMessage);
        setLoadTime(new Date().toISOString());
        setTimeout(() => {
            setTopAlertMessage(defaultTopAlertMessage);
        }, 10000)
    }, [configureSingleCLIResult])

    useEffect(() => {
        if (clonePLIConfigResult == null){
            return;
        }
        setShowCloneModal(false);
        setClonePLIPayload(null);
        setTopAlertMessage(clonePLIPayload?.cloneToAllCLIsInSameTemplate? successfulCloneAllPLITopAlertMessage: successfulCloneSelectedPLITopAlertMessage);
        setLoadTime(new Date().toISOString());
        setTimeout(() => {
            setTopAlertMessage(defaultTopAlertMessage);
        }, 10000)
    }, [clonePLIConfigResult])

    useEffect(() => {
        setGeneralPliConfigError(createPLIConfigError || pliConfigError || validatePLIError || runPLIError || configureSingleCLIError);
        if (runPLIError) {
            setLoadTime(new Date().toISOString());
        }
    }, [createPLIConfigError, pliConfigError, validatePLIError, runPLIError, configureSingleCLIError])

    useEffect(() => {
        if (pliConfigMetadata?.pli == null || pliTemplates.length === 0){
            return;
        }
        dispatch(ACTIONS.SET_SELECTED_PLI_TEMPLATE.withPayload(pliTemplates.find(x => x.value === pliConfigMetadata?.pli)))
    },[pliConfigMetadata?.pli, pliTemplates])

    useEffect(() => {
        if (calculation?.pli == null || selectedPLITemplate?.value == null) {
            return;
        }
        if (calculation.pli != selectedPLITemplate.value) {
            setTopAlertMessage(nonSuggestedPLISelectedTopAlertMessage);
        }
        else {
            setTopAlertMessage(defaultTopAlertMessage);
            // If the selected template is the recommended by Apttus but it's different than
            // the one currently saved on the PLI metadata then we still need to call the switch API
            if (calculation?.pli != null && pliConfigMetadata?.pli != calculation.pli){
                setSwitchPLITemplatePayload({calculationNumber: calculation?.calculationNumber || '', newPLI: selectedPLITemplate.value});
            }
        }
    },[calculation?.pli, selectedPLITemplate])

    useEffect(() => {
        if (switchPLITemplateResult == null){
            return;
        }
        setTopAlertMessage(null);
        setLoadTime(new Date().toISOString());
    }, [switchPLITemplateResult])
    
    const onPLITemplateChanged = (templateType?: PLITemplateType) => {
        if (templateType == null) {
            return;
        }
        dispatch(ACTIONS.SET_SELECTED_PLI_TEMPLATE.withPayload(templateType))
    }

    const onSavePLIConfigClicked = () => {
        dispatch(ACTIONS.SAVE_ALL_SECTIONS);
    }

    const onRunPLIConfigClicked = () => {
        const invalidDataSources = dataSourceRecords.filter(x => x.balancePullInProgress || x.value == null);
        if (!ArrayUtils.isNullOrEmpty(invalidDataSources)) {
            const payload = {dataSourcesInProgress: [] as string[], dataSourcesWithNoBalance: [] as string[]};

            const checkDataSourceToken = (tokenValue: string) => {
                if (invalidDataSources.some(x => x.description === tokenValue && x.balancePullInProgress)) {
                    if (!payload.dataSourcesInProgress.includes(tokenValue)) {
                        payload.dataSourcesInProgress.push(tokenValue);
                    }
                } else if (invalidDataSources.some(x => x.description === tokenValue && x.value == null)) {
                    if (!payload.dataSourcesWithNoBalance.includes(tokenValue)) {
                        payload.dataSourcesWithNoBalance.push(tokenValue);
                    }
                }
            };

            const checkStepToken = (tokenValue: string) => {
                const calcStep = calculationSteps.find(s => s.stepName === tokenValue);
                if (calcStep) {
                    calcStep.expressionTokens
                        .filter(t => t.type === ExpressionPartTypes.DATA_SOURCE)
                        .forEach(t => checkDataSourceToken(t.value));
                }
            };

            templateSections.forEach(section => {
                section.steps.forEach(step => {
                    step.expressionTokens.forEach(t => {
                        if (t.type === ExpressionPartTypes.DATA_SOURCE) {
                            checkDataSourceToken(t.value);
                        } else if (t.type === ExpressionPartTypes.STEP) {
                            checkStepToken(t.value);
                        }
                    });
                });
            });

            if (!ArrayUtils.isNullOrEmpty(payload.dataSourcesInProgress) || !ArrayUtils.isNullOrEmpty(payload.dataSourcesWithNoBalance)) {
                setInvalidRunModalPayload(payload);
                return;
            }
        }
        setRunPLIConfigPayload({calculationNumber: pliConfigMetadata?.calculationNumber || '', pliVersion: pliConfigMetadata?.pliVersion == null? -1 : pliConfigMetadata.pliVersion})
    }

    const onConfigureSingleCLIHandler = () => {
        setConfigurePLIForSingleCLIPayload({calculationNumber: pliConfigMetadata?.calculationNumber || ''})
    }

    const clearGeneralPliConfigError = () => {
        setGeneralPliConfigError(undefined);
    }

    const onClonePLIConfigSubmitted = (calculationNumber: string, cloneToAllCLIsInSameTemplate: boolean,targetCalculationNumbers: string[]) => {
        setClonePLIPayload({calculationNumber, cloneToAllCLIsInSameTemplate, targetCalculationNumbers});
    }

    const handleAddCommentClicked = (request: CalcBuilderComment) => {
        if (commentsModalPayload?.step?.isNew){
            setCommentsModalMessage(SUCCESSFUL_COMMENT_ALERT);
            request.ownerIsUnsaved = true;
            request.stepId = commentsModalPayload.step.stepId;
            request.createUser = userProfile.user;
            request.calculationVersion = selectedCalculationVersion;
            dispatch(ACTIONS.ADD_STEP_COMMENT.withPayload(request));
        }
        else {
            dispatch(ACTIONS.SET_ADD_COMMENT_PAYLOAD.withPayload({
                stepId: String(commentsModalPayload?.step.stepId || ''),
                calculationNumber: calculation?.calculationNumber || '',
                calculationVersion: selectedCalculationVersion == null ? -1 : selectedCalculationVersion,
                comment: request.comment,
            }));
        }
    }

    const handleSwitchToDefaultPLI = () => {
        dispatch(ACTIONS.SET_SELECTED_PLI_TEMPLATE.withPayload(pliTemplates.find(x => x.value === calculation?.pli)))
    }


    const handleConfirmNonDefaultPLI = () => {
        setSwitchPLITemplatePayload({calculationNumber: calculation?.calculationNumber || '', newPLI: selectedPLITemplate.value});
        setTopAlertMessage(nonSuggestedPLISwitchingTopAlertMessage);
    }

    const nonSuggestedPLISelectedTopAlertMessage = {message:"You are using a PLI that is different from the default suggestion from Apttus.", header: "", type:"warning" as AlertProps.Type, 
        action:<SpaceBetween direction='horizontal' size='m'><Button onClick={handleSwitchToDefaultPLI}>Switch to default PLI</Button><Button onClick={handleConfirmNonDefaultPLI}>Confirm</Button></SpaceBetween>
    };
    const nonSuggestedPLISwitchingTopAlertMessage = {message:"You are using a PLI that is different from the default suggestion from Apttus.", header: "", type:"warning" as AlertProps.Type, 
        action:<SpaceBetween direction='horizontal' size='m'><Button loading={true} disabled loadingText='Switching PLI...'>Switch to default PLI</Button><Button disabled onClick={handleConfirmNonDefaultPLI}>Confirm</Button></SpaceBetween>
    };
    const loadingMessage = pliMetadataLoading || pliTemplatesLoading || pliConfigLoading? "Loading PLI Configuration" : isCreatingPLIConfig? "Creating PLI Configuration" : "";
    
    return (
        <ExpandableSection data-class="polarisExpandableSection configureJESettingsSection" variant="container"
            defaultExpanded={expanded}
            headerText={
                <span className='headerText'>{CONSTANTS.ENVIRONMENT_VARIABLES.TPE_UI_STAGE !== 'prod' ? 'Configure Tested Party PLI' : 'Configure Profit Level Indicator (PLI)'}</span>
            }
            headerActions={
                showActions && !pliMetadataLoading && !isCreatingPLIConfig &&
                <SpaceBetween direction='horizontal' size='m'>
                    <Button ariaLabel="Save" disabled={allSectionsAreSaved || stepIsSaving || isValidating} onClick={onSavePLIConfigClicked}>{stepIsSaving? "Saving..." : (isValidating? "Validating..." : "Save")}</Button>
                    <Button ariaLabel="Run" disabled={!allSectionsAreSaved || isRunning} onClick={onRunPLIConfigClicked}>{isRunning? "Running..." : "Run"}</Button>
                    <ButtonDropdown
                        items={calculationTemplateMetaData?.templateId == null? 
                            [
                                {
                                    text: "Single CLI configuration",
                                    id: ConfigurePLITypes.SingleCLI,
                                }
                            ]:
                            [
                                {
                                    text: "Single CLI configuration",
                                    id: ConfigurePLITypes.SingleCLI
                                },
                                {
                                    text: "Clone to other CLIs",
                                    id: ConfigurePLITypes.CloneToMultipleCLIs
                                }
                            ]
                        }
                        disabled={!allSectionsAreValid}
                        variant="primary"
                        onItemClick={({detail})=> {
                            switch(detail.id){
                                case ConfigurePLITypes.SingleCLI:
                                    onConfigureSingleCLIHandler();
                                break;
                                case ConfigurePLITypes.CloneToMultipleCLIs:
                                    setShowCloneModal(true)
                                    break;
                            }
                        }}
                        >
                        Configure PLI
                    </ButtonDropdown>
                </SpaceBetween>
              }
            onChange={({detail}) => setShowActions(detail.expanded)}
        >
            <TPELoadingSpinner loading={pliMetadataLoading || isCreatingPLIConfig || pliTemplatesLoading || pliConfigLoading} loadingText={loadingMessage}>
                <PLIConfigProvider state={state} dispatch={dispatch} services={services}>
                    <SpaceBetween direction='vertical' size='l'>
                        { !StringUtils.isNullOrEmpty(generalPliConfigError)? 
                            <Alert type='error' statusIconAriaLabel="Error" dismissible onDismiss={clearGeneralPliConfigError}>{generalPliConfigError}</Alert> :
                            topAlertMessage && <Alert header={topAlertMessage.header} statusIconAriaLabel={topAlertMessage.type} type={topAlertMessage.type} action={topAlertMessage.action}>{topAlertMessage.message}</Alert>
                        }
                        <div>
                            <Header variant="h3" description="Use default PLI shown in the table below or click to select custom PLI">Overview</Header>
                            <Box float="right" data-class="pli-status-box">
                                <SpaceBetween direction='horizontal' size='m'>
                                    <strong>PLI Configuration Status: </strong>
                                    <StatusIndicator data-class="statusText" type={EntityStatusUtil.mapToStatusIndicatorBy(pliConfigMetadata?.pliStatus)}>
                                    {pliConfigMetadata?.pliStatus}
                                    </StatusIndicator>
                                </SpaceBetween>
                            </Box>
                        </div>
                        <PLITemplateSelector onPLITemplateChanged={onPLITemplateChanged} pliTemplates={pliTemplates}
                                             selectedPLITemplate={selectedPLITemplate}
                                             overviewSectionSteps={overviewSection?.steps}
                                             reloadPLIConfig={reloadPLIConfig}
                                             onError={setGeneralPliConfigError}/>
                        {overviewSection && <PLITemplateSection section={overviewSection} reloadPLIConfig={reloadPLIConfig} onError={setGeneralPliConfigError}/>}
                        {otherSections.map(s => <PLITemplateSection key={s.name} section={s}
                                                                    isCustomPLI={selectedPLITemplate?.value === CUSTOM_PLI}
                                                                    reloadPLIConfig={reloadPLIConfig}
                                                                    onError={setGeneralPliConfigError}/>)}
                        <TPEBasicModal
                            className="stepDeleteConfirmModal"
                            visible={removeStepModalPayload != null}
                            action={ACTION_TYPE.YES_NO}
                            title="Remove Step Confirmation"
                            onCancel={() => dispatch(ACTIONS.SET_REMOVE_STEP_PAYLOAD.withPayload(null))}
                            onConfirm={() => {
                                dispatch(ACTIONS.REMOVE_SECTION_STEP.withPayload(removeStepModalPayload));
                            }}
                            primaryButtonDisabledLabel={isDeletingStep? "Deleting...": undefined}
                        >
                            <span>Are you sure you want to remove step "{removeStepModalPayload?.step.sequence}.{removeStepModalPayload?.step.description}" from section "{removeStepModalPayload?.step.sectionName}"?</span>
                            {!StringUtils.isNullOrEmpty(deleteStepError) && <Alert type='error' statusIconAriaLabel="Delete Step Error">{deleteStepError}</Alert>}
                        </TPEBasicModal>
                        <CloneToTherCLIsModal 
                            visible={showCloneModal} 
                            isCloning={isCloningPLI} 
                            cloneError={clonePLIError} 
                            onClose={() => {
                                setShowCloneModal(false);
                            }} 
                            onSubmit={onClonePLIConfigSubmitted}/>
                        <TPEBasicModal
                            className="pliInvalidRunModal"
                            visible={invalidRunModalPayload != null}
                            action={ACTION_TYPE.OK_ONLY}
                            title="Cannot run PLI"
                            onCancel={() => setInvalidRunModalPayload(null)}
                            onConfirm={() => setInvalidRunModalPayload(null)}
                        >
                            <div>
                                {ArrayUtils.isNullOrEmpty(invalidRunModalPayload?.dataSourcesInProgress)? null :
                                    <span className='inProgressSpan'>
                                        Balance updates are in-progress for following data source(s):
                                        <ul>{invalidRunModalPayload?.dataSourcesInProgress.map(x => <li key={x}>{x}</li>)}</ul>
                                    </span>
                                }
                                {ArrayUtils.isNullOrEmpty(invalidRunModalPayload?.dataSourcesWithNoBalance)? null :
                                    <span className='noBalanceSpan'>
                                        Balance updates are needed for the following data source(s):
                                        <ul>{invalidRunModalPayload?.dataSourcesWithNoBalance.map(x => <li key={x}>{x}</li>)}</ul>
                                    </span>
                                }
                            </div>
                        </TPEBasicModal>
                        <CommentsModal
                                    header={`Step ${commentsModalPayload?.step.sequence} comments`}
                                    visible={commentsModalPayload != null}
                                    onCancel={() => dispatch(ACTIONS.SET_NEW_COMMENT_MODAL_PAYLOAD.withPayload(null))}
                                    onAddComment={handleAddCommentClicked}
                                    calcNumber={calculation?.calculationNumber as string}
                                    commentsHistory={commentsHistory}
                                    modalMessage={commentsModalMessage}
                                    isSaving={isLoadingStepComments || isSavingStepComments}
                                    readonly={true}
                                />
                    </SpaceBetween>
                    <TPEErrorWatcher services={services} errors={[configureSingleCLIError]}></TPEErrorWatcher>
                </PLIConfigProvider>
            </TPELoadingSpinner>
            <TPEErrorWatcher services={services} errors={[pliTemplatesError, createPLIConfigError, pliConfigError, pliMetadataError]} />
        </ExpandableSection>
    )
}


